<template>
  <el-dialog :visible.sync="currentlyVisible" title="Respondents" :before-close="handleCancel">
    <h3 class="title">{{ title }}</h3>
    <h2 class="subtitle">{{ subtitle }}</h2>

    <div v-if="respondents.length > 0">
      <el-table :data="getTableData">
        <el-table-column type="expand">
          <template slot-scope="props">
            <div class="row">
              <div class="col-lg-8 questions-group">
                <div
                  v-for="answer in props.row.answers.filter((a) => !a.is_profile)"
                  :key="answer.answer_id"
                  class="answer-row"
                >
                  <div class="question-title">{{ answer.question_title }}</div>
                  <div class="question-answer">
                    <span v-if="answer.input">{{ answer.input }}</span>
                    <span v-else-if="answer.choice_id">{{ answer.choice_title }}</span>
                    <span v-else-if="answer.choices_ids">
                      <ul v-for="title in answer.choices_titles" :key="title">
                        <li>{{ title }}</li>
                      </ul>
                    </span>
                  </div>
                </div>
              </div>

              <div class="col-lg-4 answers-group">
                <div
                  v-for="answer in props.row.answers.filter((a) => a.is_profile)"
                  :key="answer.answer_id"
                  class="answer-row text-right"
                >
                  <div class="question-title">{{ answer.question_title }}</div>
                  <div class="question-answer">
                    {{ answer.input ? answer.input : answer.choice_title }}
                  </div>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Date" prop="dateAnswered" />
        <el-table-column label="Name" prop="name" />
        <el-table-column label="Mobile" prop="mobileNumber"></el-table-column>
      </el-table>
    </div>
    <div class="empty" v-else-if="!loadingRespondents">
      Not enough data has been gathered yet to display any respondents.
    </div>

    <div v-if="loadingRespondents" class="loading">
      <i class="fa fa-spin fa-spinner"></i> Loading...
    </div>
    <el-button
      @click="handleLoadMore"
      v-if="respondentPageInfo.pageNumber !== respondentPageInfo.totalPages"
      class="load-more"
      type="info"
      round
      >Load more...</el-button
    >
  </el-dialog>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';

import * as surveyTypes from '@/store/modules/survey/mutation-types';

export default {
  name: 'SurveyRespondentsDialog',
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
    },
    filtering: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({}),
  computed: {
    ...mapState('survey', [
      'respondents',
      'loadingRespondents',
      'respondentMoreUrl',
      'respondentPageInfo',
    ]),

    currentlyVisible: {
      get() {
        return this.isVisible;
      },

      set(newValue) {
        this.$emit('onVisiblityChange', newValue);

        if (!newValue) {
          this.$store.commit(`survey/${surveyTypes.CLEAR_SURVEY_RESPONDENTS}`);
        }
      },
    },

    getTableData() {
      const resp = this.respondents.map(respondent => ({
        id: respondent.id,
        uuid: respondent.uuid,
        dateAnswered: moment(respondent.dateAnswered).format('lll'),
        mobileNumber: this.formatMobileNumber(respondent.mobileNumber),
        answers: this.getAnswers(respondent.answer),
        ...this.mapProfiles(respondent.answer),
      }));
      return resp;
    },
  },
  methods: {
    handleCancel() {
      this.currentlyVisible = false;
    },

    getAnswers(answers) {
      return answers.map(answer => ({
        input: answer.input,
        answer_id: answer.id,
        choice_id: answer.choice === null ? '' : answer.choice.id,
        is_profile: answer.question.isProfile,
        category_id: answer.question.categoryId,
        choices_ids: answer.choices.map(choice => choice.id),
        question_id: answer.question.id,
        choice_title: answer.choice === null ? '' : answer.choice.title,
        is_analytics: answer.question.isAnalytics,
        profile_field: answer.question.profileField,
        question_type: answer.question.questionType,
        choices_titles: answer.choices.map(choice => choice.title),
        question_title: answer.question.title,
      }));
    },

    mapProfiles(respondent) {
      return respondent
        .filter(answer => answer.is_profile)
        .reduce(
          (acc, ans) => ({
            ...acc,
            [ans.profile_field]: ans.input ? ans.input : ans.choice_title,
          }),
          {},
        );
    },

    formatMobileNumber(mobileNumber) {
      if (!mobileNumber) {
        return mobileNumber;
      }
      const num = mobileNumber.replace(/\D+/g, '');
      return `${num.substring(0, 3)} ${num.substring(3, 6)} ${num.substring(6, 10)}`;
    },

    handleLoadMore() {
      this.$store.dispatch('survey/getRespondents', this.filtering);
    },
  },
  watch: {
    filtering() {
      if (Object.keys(this.filtering).length > 0) {
        this.$store.dispatch('survey/getRespondents', this.filtering);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.questions-group {
  border-right: 1px solid #ccc;
}
.answer-row {
  margin-bottom: 12px;
}
.question-title {
  font-weight: bold;
  font-size: 0.8em;
}
.question-answer {
  margin-top: 5px;
  padding: 5px 0;
  border-bottom: 1px solid #eee;
}
.loading {
  padding: 30px;
  font-size: 1.4em;
  text-align: center;
}
.subtitle {
  margin-top: 12px;
}
.load-more {
  margin-top: 25px;
  width: 100%;
}
.empty {
  margin-top: 20px;
  text-align: center;
  font-size: 1.6em;
  color: #ccc;
  text-shadow: 2px 2px 2px #efefef;
}
</style>



// WEBPACK FOOTER //
// SurveyRespondentsDialog.vue?998bf02e